import * as Apollo from "@apollo/client";

import * as ErrorMessage from "~/ErrorMessage";

/**
 * Mirrors a subset of elogs-shared's `ServiceErrorCodes` enum
 * (`packages/elogs-shared/src/errors/errors.ts` in elogs-monorepo). Not derived
 * from the GraphQL schema: elogs-mobile-api stitches `extensions.code` onto
 * errors at runtime (`formatServiceError` in its `apolloServer.ts`) rather than
 * declaring it in the SDL, so it isn't introspectable/codegen-able from here.
 * Hand-maintained — add a code once its message is confirmed safe to show as-is.
 */
export enum Code {
  BadUserInput = "BAD_USER_INPUT",
  UnprocessableEntity = "UNPROCESSABLE_ENTITY",
  /**
   * Malformed or out-of-range arguments — a reporting window over the 59-day
   * limit, a start after its end, a date that isn't a real calendar day. The
   * message names the offending argument and value.
   */
  IllegalArgument = "ILLEGAL_ARGUMENT",
  /**
   * A submission for this driver was accepted moments ago and the API is
   * refusing a duplicate filing. The message states the debounce window, which
   * is the only thing that would let the submitter decide whether to wait.
   */
  Conflict = "CONFLICT",
}

const DISPLAYABLE_CODES: ReadonlySet<string> = new Set(Object.values(Code));

/** A mutation error whose `message` came straight from the backend and is safe to show the user as-is. */
export type DisplayableError = Error & { readonly displayable: true };

export const isDisplayableError = (error: unknown): error is DisplayableError =>
  error instanceof Error &&
  (error as Partial<DisplayableError>).displayable === true;

const toDisplayableError = (message: string): DisplayableError =>
  Object.assign(Error(message), { displayable: true as const });

/**
 * A `DisplayableError` carrying the backend's own message, if `error` is a
 * GraphQL error whose `extensions.code` is allow-listed above — otherwise `undefined`.
 */
export const fromMutationError = (
  error: unknown
): DisplayableError | undefined => {
  if (!(error instanceof Apollo.ApolloError)) return undefined;

  const [gqlError] = error.graphQLErrors;
  const code = gqlError?.extensions?.code as string | undefined;

  return code && DISPLAYABLE_CODES.has(code)
    ? toDisplayableError(gqlError.message)
    : undefined;
};

/**
 * The backend's own message when it is allow-listed, and the generic connection
 * message otherwise — the same one `Request.fromConfig` produces for REST, so a
 * failure reads identically whichever transport it came from.
 */
export const toError = (error: unknown): Error =>
  fromMutationError(error) ??
  Error(
    `Something is wrong with your connection, ${ErrorMessage.Remedy.Retry.later()} or ${ErrorMessage.Remedy.contactSupport()}.`
  );
